/**
 * WhitelistDialog.tsx — 设备白名单弹窗（注册名 whitelist，ADM-USR-S005，root 专属）
 *
 * 1:1 移植自原型 design/Novel-admin/src/dialogs2.jsx 1507–1559，已接真：
 * 列表读 useDeviceWhitelist()（GET /api/v1/users/device-whitelist，['device-whitelist']）；
 * 「添加」逐 device_id 走 POST /api/v1/users/device-whitelist（usersApi.addDevice），
 * 「移除」二次确认后走 DELETE /api/v1/users/device-whitelist/:id（usersApi.removeDevice，
 * 传白名单行 UUID 而非 device_id），均成功后 invalidate ['device-whitelist'] + 真值 toast，
 * 失败错误 toast（不假成功）。原型本地 seed 行（WHITELIST_DEVICES / WHITELIST_SEED_COUNT）已删除。
 * 真 DTO 无「归属用户 / 操作员」字段（契约无 device↔user 关联）→ 列以「—」呈现，不编造。
 * 「备注」按钮契约虽支持 note 字段，备注编辑器按计划 §五裁决 NOT-in-scope → disabled 标注。
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useAppScope } from '~/app/app-scope-store';
import { useDeviceWhitelist } from '~/data/queries';
import type { DeviceWhitelistRow } from '~/data/queries';
import { usersApi } from '~/hooks/use-users';
import { fmtDate } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { ConfirmDialog, Dialog } from '~/ui/Dialog';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';

// 二次确认参数（替代原型 window.openDialog('confirm', ...)，复刻 UsersPage 受控 confirm 模式）
interface ConfirmState {
  title: string;
  message: string;
  onConfirm: () => void;
}

const NOT_IN_SCOPE_TITLE =
  '待契约（NOT-in-scope，见 docs/plans/2026-06-12-web-real-api-integration-plan.md §五）';

export function WhitelistDialog({ onClose }: { onClose: () => void }) {
  const [val, setVal] = useState('');
  // 设备白名单 per-App（张培 PR332）：本页入口 root-gate（UsersPage），后端 RBAC viewer+/operator+ 权威。
  // 白名单按 App 隔离（key=(app_id,device_id)）→ 按顶栏 app scope 过滤 + 新增归属当前 App（'all' 时需先选具体 App）。
  const appScope = useAppScope((s) => s.appId);
  const scopedAppId = appScope === 'all' ? undefined : appScope;
  const { data: rows, isLoading } = useDeviceWhitelist(scopedAppId);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const queryClient = useQueryClient();

  const addMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      // 逐 device_id 真实 POST；allSettled 聚合成功 / 失败计数（部分失败不吞）
      const results = await Promise.allSettled(
        ids.map((id) => usersApi.addDevice({ app_id: appScope, device_id: id, reason: null })),
      );
      return {
        added: results.filter((r) => r.status === 'fulfilled').length,
        failed: results.filter((r) => r.status === 'rejected').length,
      };
    },
    onSuccess: ({ added, failed }) => {
      if (added > 0) {
        toast(
          failed > 0
            ? `已添加 ${added} 个设备，${failed} 个失败`
            : `已添加 ${added} 个设备到白名单`,
          { tone: failed > 0 ? 'warn' : 'pos', icon: failed > 0 ? 'IconAlert' : 'IconCheck' },
        );
        setVal('');
      } else {
        toast('设备添加失败', { tone: 'neg', icon: 'IconAlert' });
      }
      queryClient.invalidateQueries({ queryKey: ['device-whitelist'] });
    },
    onError: (err) => {
      toast(err instanceof Error ? err.message : '设备添加失败', {
        tone: 'neg',
        icon: 'IconAlert',
      });
    },
  });

  const removeMutation = useMutation({
    // DELETE 走白名单行 UUID（DeviceWhitelistRow.id），非 device_id
    mutationFn: (row: DeviceWhitelistRow) => usersApi.removeDevice(row.id),
    onSuccess: (_res, row) => {
      toast(`已移除 ${row.deviceId}`, { tone: 'warn' });
      queryClient.invalidateQueries({ queryKey: ['device-whitelist'] });
    },
    onError: (err, row) => {
      toast(`${row.deviceId} · ${err instanceof Error ? err.message : '移除失败'}`, {
        tone: 'neg',
        icon: 'IconAlert',
      });
    },
  });

  // 添加：逗号 / 空白切分 → 批内去重 + 排除已存在 + 限 200 上限（客户端预检，服务端为权威）
  const add = () => {
    // 白名单按 App 隔离（key=(app_id,device_id)）：'all' 聚合态无归属 App → 提示先在顶栏选具体 App
    if (appScope === 'all') {
      toast('请先在顶栏选择具体 App（白名单按 App 隔离）', { tone: 'warn', icon: 'IconAlert' });
      return;
    }
    const ids = val
      .split(/[,，\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!ids.length) {
      toast('请先输入设备 ID', { tone: 'warn', icon: 'IconAlert' });
      return;
    }
    const existing = new Set(rows.map((r) => r.deviceId));
    const capacity = Math.max(0, 200 - rows.length); // 上限 200
    const toAdd = [...new Set(ids)].filter((id) => !existing.has(id)).slice(0, capacity);
    if (!toAdd.length) {
      toast(capacity === 0 ? '白名单已达上限 200，未添加' : '设备已在白名单，无新增', {
        tone: 'warn',
        icon: 'IconAlert',
      });
      return;
    }
    addMutation.mutate(toAdd);
  };

  // 移除：二次确认后真实 DELETE
  const remove = (row: DeviceWhitelistRow) =>
    setConfirm({
      title: `移除设备 ${row.deviceId}？`,
      message: '移除后该设备将重新受服务端固定设备封禁规则约束。',
      onConfirm: () => removeMutation.mutate(row),
    });

  return (
    <>
      <Dialog
        width={680}
        title="设备白名单"
        subtitle="白名单内设备不触发服务端固定设备封禁规则"
        icon="IconShield"
        onClose={onClose}
        closeOnEsc={!confirm}
        footer={
          <button className="btn" onClick={onClose} style={{ marginLeft: 'auto' }}>
            关闭
          </button>
        }
      >
        <div style={{ padding: 18 }}>
          <div className="hstack" style={{ gap: 8, marginBottom: 12 }}>
            <input
              className="input mono"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') add();
              }}
              placeholder="设备 ID (d_xxxxx) 或贴入多个 · 逗号分隔"
              style={{ flex: 1 }}
            />
            <button
              className="btn"
              disabled
              title={NOT_IN_SCOPE_TITLE}
              style={{ opacity: 0.55, cursor: 'not-allowed' }}
            >
              备注
            </button>
            <button className="btn btn-accent" onClick={add} disabled={addMutation.isPending}>
              添加
            </button>
          </div>
          <div className="card-flat" style={{ overflow: 'hidden' }}>
            <Table className="tbl">
              <thead>
                <tr>
                  <th>设备 ID</th>
                  <th>归属用户</th>
                  <th>备注</th>
                  <th>操作员</th>
                  <th>添加时间</th>
                  <th style={{ width: 36 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id}>
                    <td className="mono">{d.deviceId}</td>
                    {/* 契约无 device↔user 关联 / 操作员字段 → 「—」呈现，不编造 */}
                    <td style={{ fontSize: 12 }}>—</td>
                    <td style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>{d.reason || '—'}</td>
                    <td className="mono" style={{ fontSize: 11 }}>
                      —
                    </td>
                    <td className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                      {fmtDate(d.createdAt)}
                    </td>
                    <td>
                      <button
                        className="btn btn-ghost btn-sm"
                        title="移除"
                        disabled={removeMutation.isPending}
                        onClick={() => remove(d)}
                      >
                        <Icons.IconX size={11} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {rows.length === 0 && (
              <div
                style={{ padding: 24, textAlign: 'center', color: 'var(--fg-faint)', fontSize: 12 }}
              >
                {isLoading ? '加载中…' : '暂无白名单设备'}
              </div>
            )}
          </div>
          <div
            style={{
              marginTop: 12,
              padding: 10,
              background: 'var(--bg-subtle)',
              borderRadius: 5,
              fontSize: 11.5,
              color: 'var(--fg-muted)',
            }}
          >
            <Icons.IconInfo
              size={11}
              style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--info)' }}
            />
            白名单总计 {rows.length} 个设备 · 上限 200
          </div>
        </div>
      </Dialog>
      {/* ConfirmDialog 渲染在 Dialog 之后：两者都 portal 同 z-index 的 .dialog-backdrop，后挂载的在上层，
          确保二次确认浮在白名单弹窗之上、可点击（codex #14 bot — 否则被父弹窗 backdrop 盖住、拦截点击）*/}
      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          tone="danger"
          confirmLabel="确认移除"
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}
    </>
  );
}
